import { ElementKind } from '@domain/elements';
import { ImageSizeMode } from '@domain/image';
import { RadiusType } from '@domain/style';
import { getBrowserVersion, isEdge, isFirefox, isIOS, isSafari, preciseIOSVersion } from '@studio/utils/ad/browser';
import { insertAfter } from '@studio/utils/ad/dom';
import { ErrorMessage } from '@studio/utils/errors/error-message.enum';
import { aspectRatioScale, isSameSize } from '@studio/utils/geom';
import { MaskingRenderMap } from '@studio/utils/masking-rendermap';
import { Matrix } from '@studio/utils/matrix';
import { PromiseResolver, loadImagePromise } from '@studio/utils/promises';
import { sanitizeUrl } from '@studio/utils/sanitizer';
import { createRoundedShape } from '@studio/utils/svg-utils';
import { isFiniteNumber, isNumber, toDegrees } from '@studio/utils/utils';
import { toHEX } from './color.utils';
import { getImageExtension, ImageOptimizerUrlBuilder } from './image-optimizer';
import { isHidden, isMaskNode } from './nodes';
import { isCreativeNode, isImageNode, isMaskedNode, isMaskingSupported, isUsedInMask, isVideoNode, isViewElementNode } from './nodes/helpers';
const SVG_IMAGE_OPTIONS = {
    [ImageSizeMode.Fit]: 'xMidYMid meet',
    [ImageSizeMode.Fill]: 'xMidYMid slice',
    [ImageSizeMode.Stretch]: 'none'
};
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';
const matrixValueRegex = /matrix3d\((.+)\)/;
var IdSuffix;
(function (IdSuffix) {
    IdSuffix["Shape"] = "-shape";
    IdSuffix["Group"] = "-group";
    IdSuffix["LinearGradient"] = "-linear-gradient";
    IdSuffix["Border"] = "-border";
    IdSuffix["Clip"] = "-clip";
    IdSuffix["Mask"] = "-mask";
    IdSuffix["Image"] = "-image";
    IdSuffix["Shadow"] = "-shadow";
})(IdSuffix || (IdSuffix = {}));
export class ImageError extends Error {
    constructor(msg, url, error) {
        super(msg);
        this.url = url;
        this.error = error;
        // Set the prototype explicitly.
        Object.setPrototypeOf(this, ImageError.prototype);
    }
}
/**
Elements need to be added in this order
1. groupElement.appendChild(shapeElement);
2. groupElement.appendChild(imageSvgElement);
3. groupElement.appendChild(loadingElement);
4. gradientElement.appendChild(stopElement);
5. defsElement.appendChild(gradientElement);
6. clipPathElement.appendChild(clipUseElement);
7. defsElement.appendChild(borderElement);
8. defsElement.appendChild(clipPathElement);
9. groupElement.appendChild(borderUseElement);
10. defsElement.appendChild(filterElement);
11. svgElement!.appendChild(useElement);
12. svgElement.appendChild(defsElement);
13. svgElement.appendChild(groupElement);
14. node.__rootElement!.appendChild(svgElement);
 */
export class SVGBackground {
    get id() {
        return this._id;
    }
    get shapeElement() {
        return this._shapeElement;
    }
    get foreignObject() {
        return this._foreignObject;
    }
    constructor(_window, _element, _renderer, _creativeConfig) {
        var _a;
        this._window = _window;
        this._element = _element;
        this._renderer = _renderer;
        this._creativeConfig = _creativeConfig;
        this._shadowFilterElements = [];
        this._shadowUseElements = [];
        this._gradientElementStops = [];
        this._shadows = [];
        this._shadowType = SvgShadowType.None;
        this._iosVideoMaskApplied = false;
        this._document = this._window.document;
        this._element = Object.assign(Object.assign({}, _element), { width: Math.round(_element.width), height: Math.round(_element.height) });
        this._baseUrl = this._document.querySelector('head base')
            ? this._window.location.href.replace(this._window.location.hash, '')
            : '';
        const elementId = isViewElementNode(this._element) ? this._element.elementCid : 'creative';
        this._id = `${elementId !== null && elementId !== void 0 ? elementId : 'unset'}-${Math.floor(Math.random() * 1000000)}`;
        this._shapeElementKind = this._getShape();
        if (!isCreativeNode(_element)) {
            this._viewElement = _element;
        }
        // Note that data doesn't exist when this is used as creative background
        this._data = this._element.__data;
        this._createSVG();
        this._shapeElement.setAttribute('d', createRoundedShape(this._element.width, this._element.height, (_a = this._radius) !== null && _a !== void 0 ? _a : this.getDefaultRadius()));
    }
    render_m(element, overrides) {
        let promise;
        if (overrides) {
            this._styles = Object.assign(Object.assign({}, element), overrides); // A bit expensive
        }
        else {
            this._styles = element;
        }
        this._element = Object.assign(Object.assign({}, element), { width: Math.round(element.width), height: Math.round(element.height) });
        if (this._shapeElementKind === ElementKind.Image) {
            promise = this._setImageUrl(this._styles.imageUrl);
        }
        this._setFill(this._styles.fill);
        this._setBorder(this._styles.border);
        this._setSizeAndRadius(this._styles);
        this._setShadows(this._styles.shadows);
        this._setMasking();
        return promise;
    }
    setResponsive() {
        if (this._width > this._height) {
            this._svgElement.style.height = 'auto';
            this._svgElement.style.width = '100%';
        }
        else {
            this._svgElement.style.height = '100%';
            this._svgElement.style.width = 'auto';
        }
        this.setViewBox({ x: 0, y: 0 }, { width: this._width, height: this._height });
    }
    setViewBox(position, size) {
        this._svgElement.setAttribute('viewBox', `${position.x} ${position.y} ${size.width} ${size.height}`);
        console.debug('[SVGBackground] setViewBox');
    }
    _getShape() {
        var _a;
        switch ((_a = this._element) === null || _a === void 0 ? void 0 : _a.kind) {
            case ElementKind.Ellipse:
            case ElementKind.Image:
                return this._element.kind;
            default:
                return ElementKind.Rectangle;
        }
    }
    _createSVG() {
        var _a;
        if (this._svgElement) {
            throw new Error('Background already added');
        }
        const document = this._document;
        const groupElement = document.createElementNS(SVG_NAMESPACE, 'g');
        const shapeElement = document.createElementNS(SVG_NAMESPACE, this._getShape() === 'ellipse' ? 'ellipse' : 'path');
        const defsElement = document.createElementNS(SVG_NAMESPACE, 'defs');
        const svgElement = document.createElementNS(SVG_NAMESPACE, 'svg');
        const style = svgElement.style;
        svgElement.setAttribute('xmlns', SVG_NAMESPACE); // Needed by serializer
        style.position = 'absolute';
        style.left = '0';
        style.top = '0';
        style.width = '100%';
        style.height = '100%';
        style.overflow = 'visible';
        style.zIndex = '0';
        // Hack to avoid jagged edges on image export on rotated elements
        if (this._shouldAddOutline()) {
            style.outline = '1px solid transparent';
        }
        shapeElement.id = this._getId(IdSuffix.Shape);
        shapeElement.setAttribute('fill', 'transparent');
        groupElement.id = this._getId(IdSuffix.Group);
        groupElement.appendChild(shapeElement);
        svgElement.appendChild(defsElement);
        svgElement.appendChild(groupElement);
        this._shapeElement = shapeElement;
        this._defsElement = defsElement;
        this._groupElement = groupElement;
        this._svgElement = svgElement;
        if (isVideoNode(this._data)) {
            this._foreignObject = this._createForeignObject();
        }
        // Add it as first child in the DOM to make it render behind all other items
        (_a = this._element.__rootElement) === null || _a === void 0 ? void 0 : _a.prepend(svgElement);
        return svgElement;
    }
    _createForeignObject() {
        const foreignObject = this._document.createElementNS(SVG_NAMESPACE, 'foreignObject');
        foreignObject.setAttribute('fill', 'transparent');
        const innerWrapper = this._document.createElement('div');
        foreignObject.appendChild(innerWrapper);
        this._groupElement.appendChild(foreignObject);
        return foreignObject;
    }
    _showLoader() {
        if (!this._loaderElement) {
            this._loaderElement = createSVGLoaderImage(this._styles, this._document);
        }
        if (!this._loaderElement.parentNode) {
            this._groupElement.appendChild(this._loaderElement);
        }
    }
    _hideLoader() {
        var _a;
        if ((_a = this._loaderElement) === null || _a === void 0 ? void 0 : _a.parentNode) {
            this._groupElement.removeChild(this._loaderElement);
        }
    }
    _shouldAddOutline() {
        var _a;
        if (this._creativeConfig.environment === 4 /* AdEnvironment.ImageGenerator */ &&
            ((_a = this._element) === null || _a === void 0 ? void 0 : _a.rotationZ)) {
            if (this._element.rotationZ !== 0) {
                return true;
            }
        }
        return false;
    }
    _setSizeAndRadius(styles) {
        const { width, height, radius } = styles;
        if (!isFiniteNumber(width) || !isFiniteNumber(height)) {
            throw new Error('width and height must be numbers');
        }
        if (width !== this._width || height !== this._height) {
            this._width = width;
            this._height = height;
            const overflow = this._getShadowOverflow();
            this._svgElement.setAttribute('width', `${width + overflow * 2}`);
            this._svgElement.setAttribute('height', `${height + overflow * 2}`);
            if (this._shapeElementKind === ElementKind.Ellipse) {
                // Center
                this._shapeElement.setAttribute('cx', `${overflow + width / 2}`);
                this._shapeElement.setAttribute('cy', `${overflow + height / 2}`);
                // Radius
                this._shapeElement.setAttribute('rx', `${width / 2}`);
                this._shapeElement.setAttribute('ry', `${height / 2}`);
            }
            else if (this._foreignObject) {
                this._foreignObject.setAttribute('height', `${height}`);
                this._foreignObject.setAttribute('width', `${width}`);
            }
            this._setBorderSize();
            this._setGradientSize();
            this._setShadowSize();
        }
        this._setRadius(radius);
        this._setImageSize();
    }
    _setGradientSize() {
        if (this._gradientElement) {
            if (!this._gradientPosition || !isNumber(this._width) || !isNumber(this._height)) {
                throw new Error('No gradient position provided');
            }
            const x1 = this._gradientPosition.from.x * this._width;
            const y1 = this._gradientPosition.from.y * this._height;
            const x2 = this._gradientPosition.to.x * this._width;
            const y2 = this._gradientPosition.to.y * this._height;
            this._gradientElement.setAttribute('x1', `${x1}`);
            this._gradientElement.setAttribute('y1', `${y1}`);
            this._gradientElement.setAttribute('x2', `${x2}`);
            this._gradientElement.setAttribute('y2', `${y2}`);
            console.debug('[SVGBackground] _setGradientSize');
        }
    }
    _fillToPosition(fill) {
        var _a;
        if ((_a = fill === null || fill === void 0 ? void 0 : fill.stops) === null || _a === void 0 ? void 0 : _a.length) {
            const from = fill.start.position;
            const to = fill.end.position;
            return { from: Object.assign({}, from), to: Object.assign({}, to) };
        }
    }
    _createLinearGradient() {
        if (!this._gradientElement) {
            const gradientId = this._getId(IdSuffix.LinearGradient);
            const gradientElement = this._document.createElementNS(SVG_NAMESPACE, 'linearGradient');
            gradientElement.id = gradientId;
            gradientElement.setAttribute('gradientUnits', 'userSpaceOnUse');
            const firstStop = this._document.createElementNS(SVG_NAMESPACE, 'stop');
            gradientElement.appendChild(firstStop);
            const secondStop = this._document.createElementNS(SVG_NAMESPACE, 'stop');
            gradientElement.appendChild(secondStop);
            this._gradientElementStops.push(firstStop, secondStop);
            this._gradientElement = gradientElement;
        }
        return this._gradientElement;
    }
    calculateRadius(radius) {
        const minValue = Math.min(this._element.width, this._element.height);
        const maxRadiusValue = Math.ceil(minValue / 2);
        const applyMaxCap = (currentRadius) => currentRadius > maxRadiusValue ? maxRadiusValue : currentRadius;
        return {
            type: radius.type,
            topLeft: applyMaxCap(radius.topLeft),
            topRight: applyMaxCap(radius.topRight),
            bottomRight: applyMaxCap(radius.bottomRight),
            bottomLeft: applyMaxCap(radius.bottomLeft)
        };
    }
    getDefaultRadius() {
        return {
            type: RadiusType.Joint,
            topLeft: 0,
            topRight: 0,
            bottomRight: 0,
            bottomLeft: 0
        };
    }
    _setRadius(radius) {
        var _a;
        if (this._shapeElementKind === ElementKind.Ellipse) {
            return;
        }
        const newRadius = this.calculateRadius(radius || this.getDefaultRadius());
        const newShape = createRoundedShape(Math.round(this._element.width), Math.round(this._element.height), newRadius);
        if (newShape === this._radiusShape) {
            return;
        }
        this._radius = newRadius;
        this._radiusShape = newShape;
        this._shapeElement.setAttribute('d', newShape);
        (_a = this._borderElement) === null || _a === void 0 ? void 0 : _a.setAttribute('d', newShape);
        console.debug('[SVGBackground] _setRadius');
    }
    _setFill(fill) {
        const gradientId = this._getId(IdSuffix.LinearGradient);
        const colorString = fill === null || fill === void 0 ? void 0 : fill.toString();
        if (colorString === this._color) {
            return;
        }
        this._color = colorString;
        if (fill) {
            const stops = fill.stops || [];
            // See more here: https://codepen.io/NV/pen/jcnmK
            if (fill.type === 'linear-gradient' && stops.length) {
                const gradientElement = this._createLinearGradient();
                this._gradientPosition = this._fillToPosition(fill);
                if (this._width && this._height) {
                    this._setGradientSize();
                }
                stops.forEach((stop, index) => {
                    const color = stop.color;
                    const stopElement = this._gradientElementStops[index];
                    stopElement.setAttribute('offset', `${stop.offset}%`);
                    stopElement.setAttribute('stop-color', toHEX(color));
                    stopElement.setAttribute('stop-opacity', ((color.alpha / 100) * (fill.alpha / 100)).toString());
                });
                // Add to DOM if not already added
                if (!gradientElement.parentNode) {
                    this._defsElement.appendChild(gradientElement);
                }
                // Hack for Safari
                if (isSafari) {
                    this._shapeElement.setAttribute('fill', `url('${this._baseUrl}#${gradientId}')`);
                }
                else {
                    this._shapeElement.setAttribute('fill', `url(#${gradientId})`);
                }
            }
            // Solid color
            else {
                this._shapeElement.setAttribute('fill', colorString);
            }
        }
        else {
            this._shapeElement.setAttribute('fill', 'transparent');
        }
        console.debug('[SVGBackground] _setFill');
    }
    _setBorderSize() {
        if (this._borderElement) {
            this._borderElement.setAttribute('width', `${this._width}`);
            this._borderElement.setAttribute('height', `${this._height}`);
            console.debug('[SVGBackground] _setBorderSize');
        }
    }
    /**
     * Only for images and videos other elements have border applied on the shape itself
     */
    _createBorderAndClipElement() {
        if (this._borderElement) {
            return this._borderElement;
        }
        const borderId = this._getId(IdSuffix.Border);
        const clipId = this._getId(IdSuffix.Clip);
        const borderElement = this._document.createElementNS(SVG_NAMESPACE, 'path');
        borderElement.setAttribute('x', '0');
        borderElement.setAttribute('y', '0');
        borderElement.setAttribute('fill', 'transparent');
        borderElement.id = borderId;
        // Used to mask image to create a radius effect
        this._clipPathElement = this._document.createElementNS(SVG_NAMESPACE, 'clipPath');
        this._clipPathElement.id = clipId;
        const clipUseElement = this._document.createElementNS(SVG_NAMESPACE, 'use');
        clipUseElement.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', `${this._baseUrl}#${borderId}`);
        clipUseElement.style.pointerEvents = 'none';
        this._clipPathElement.appendChild(clipUseElement);
        this._borderUseElement = this._document.createElementNS(SVG_NAMESPACE, 'use');
        this._borderUseElement.style.pointerEvents = 'none';
        this._borderUseElement.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', `${this._baseUrl}#${borderId}`);
        const clipPathURL = `url(${this._baseUrl}#${clipId})`;
        if (this._imageElement) {
            this._imageElement.setAttribute('clip-path', clipPathURL);
        }
        if (this._foreignObject) {
            this._foreignObject.setAttribute('clip-path', clipPathURL);
            /**
             * Rounded corners on video elements does not work in safari
             * without using the CSS clip-path style
             * */
            const divChild = this._foreignObject.firstElementChild;
            divChild.style.clipPath = clipPathURL;
            if (isSafari) {
                divChild.style.height = 'inherit';
            }
        }
        this._defsElement.appendChild(borderElement);
        this._defsElement.appendChild(this._clipPathElement);
        this._groupElement.appendChild(this._borderUseElement);
        this._borderElement = borderElement;
        return this._borderElement;
    }
    _setMasking() {
        var _a, _b;
        const dataNode = this._data;
        if (!dataNode || !isUsedInMask(dataNode)) {
            return;
        }
        const { isMask, elementId } = (_a = dataNode.masking) !== null && _a !== void 0 ? _a : {};
        this._moveSvgGroupElement(isMask);
        this._addMaskedImageContainer(this._imageElement);
        if (this._shouldUpdateMask()) {
            const targetViewElement = elementId
                ? this._renderer.getViewElementById(elementId)
                : undefined;
            if (elementId) {
                const node = (_b = targetViewElement === null || targetViewElement === void 0 ? void 0 : targetViewElement.__data) !== null && _b !== void 0 ? _b : this._renderer.creativeDocument.findNodeById_m(elementId);
                if (node && isHidden(node)) {
                    this.removeMasking_m(true);
                }
            }
            if (isMask || elementId) {
                const maskingData = this._getMaskingData();
                this._applyMaskingStyles(maskingData);
                SVGBackground._maskingRenderMap.set_m(dataNode, true);
                this._maskingData = maskingData;
            }
            if (!targetViewElement) {
                return;
            }
            this._createMaskedElement(targetViewElement);
        }
    }
    _createMaskedElement(targetViewElement) {
        var _a;
        const maskId = this._getId(IdSuffix.Mask);
        if (!this._maskElement) {
            this._maskElement = this._document.createElementNS(SVG_NAMESPACE, 'mask');
            this._maskElement.id = maskId;
            this._maskElement.setAttribute('mask-type', 'alpha'); // Needed for iOS/OSX <= 16
            this._defsElement.appendChild(this._maskElement);
        }
        if (!this._maskUseElement) {
            const id = (_a = targetViewElement.__svgBackground) === null || _a === void 0 ? void 0 : _a.id;
            const targetShapeId = isImageNode(targetViewElement)
                ? id + IdSuffix.Image
                : id + IdSuffix.Shape;
            const maskUseElement = this._document.createElementNS(SVG_NAMESPACE, 'use');
            maskUseElement.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', `${this._baseUrl}#${targetShapeId}`);
            maskUseElement.style.pointerEvents = 'none';
            this._maskUseElement = maskUseElement;
            this._maskElement.appendChild(maskUseElement);
            this._maskUseElement.style.transformOrigin = 'center center';
            this._maskUseElement.style.transformBox = 'border-box';
            // Masking has to be applied to the foreignObject style for Safari/iOS for masking on video to work.
            if ((isSafari || isIOS) && isVideoNode(this._viewElement) && this._foreignObject) {
                const foreignChild = this._foreignObject.firstElementChild;
                foreignChild.style.height = '100%';
                // iOS 16 and below: mask-image CSS property doesn't work on HTML elements inside foreignObject.
                // Use clip-path approach instead to constrain the video within the mask bounds.
                const isIOS16OrBelow = isIOS && preciseIOSVersion.simple < 17;
                if (isIOS16OrBelow) {
                    this._applyLegacyIOSVideoMasking(foreignChild, targetViewElement);
                }
                else {
                    // Safari macOS and iOS 17+ work with mask-image on the div
                    foreignChild.style.maskImage = `url(#${maskId})`;
                    foreignChild.style.maskMode = 'alpha';
                    foreignChild.style.maskRepeat = `no-repeat`;
                }
            }
            // mask with url ref is applied to SVGGroupElement as well to properly mask rotations
            // If Safari didn't need mask applied on the SVGElement then only SVGGroupElement would be needed mask refs/styles
            this._groupElement.setAttribute('mask', `url(#${maskId})`);
            this._groupElement.style.maskMode = 'alpha'; // Needed for non iOS/OSX <= 16
            this._groupElement.style.maskRepeat = 'no-repeat';
        }
        if (this._maskUseElement) {
            const x = targetViewElement.x - this._viewElement.x;
            const y = targetViewElement.y - this._viewElement.y;
            this._maskUseElement.style.transform = `translate(${x}px, ${y}px)`;
            // Also update the iOS mask position if it exists
            if (this._iosVideoMaskApplied && this._foreignObject) {
                const foreignChild = this._foreignObject.firstElementChild;
                if (foreignChild) {
                    foreignChild.style.maskPosition = `${x}px ${y}px`;
                }
            }
        }
    }
    /** `SVGElement`(s) inside of `<defs>` are not rendered but still usable for e.g masking */
    _moveSvgGroupElement(setAsDefs = false) {
        const defsElementHasGroup = this._defsElement.contains(this._groupElement);
        if (setAsDefs && !defsElementHasGroup) {
            this._defsElement.insertBefore(this._groupElement, null);
        }
        else if (!setAsDefs && defsElementHasGroup) {
            this._svgElement.insertBefore(this._groupElement, null);
        }
    }
    // Masked images need to be wrapped in a g element for rotations + panning to work correctly
    _addMaskedImageContainer(imageSvg) {
        const dataNode = this._data;
        const isMasked = dataNode && isMaskedNode(dataNode);
        if (this._imageContainerElement || !imageSvg || !isMasked) {
            return;
        }
        const svgGElement = this._document.createElementNS(SVG_NAMESPACE, 'g');
        imageSvg.replaceWith(svgGElement);
        svgGElement.appendChild(imageSvg);
        this._imageContainerElement = svgGElement;
    }
    /**
     * Applies masking for video elements on iOS 16 and earlier.
     *
     * iOS 16 Safari doesn't support `mask-image` with SVG URL references on HTML elements
     * inside `<foreignObject>`. Instead, we use CSS `mask-image` with either:
     * - The actual raster image URL (for PNG/SVG masks with alpha)
     * - An inline SVG data URL containing the mask shape (for shape masks and JPG masks)
     *
     * This allows both border radius (via CSS clip-path) and mask shape to work together.
     */
    _applyLegacyIOSVideoMasking(foreignChild, targetViewElement) {
        const targetSvgBackground = targetViewElement.__svgBackground;
        // Calculate the position offset relative to the video element
        const x = targetViewElement.x - this._viewElement.x;
        const y = targetViewElement.y - this._viewElement.y;
        if (isImageNode(targetViewElement)) {
            const maskUrl = targetViewElement.imageUrl;
            if (maskUrl) {
                const originalMaskUrl = ImageOptimizerUrlBuilder.getOriginalUrl(maskUrl) || maskUrl;
                const extension = getImageExtension(originalMaskUrl) || getImageExtension(maskUrl);
                const supportsAlphaMask = extension === 'png' || extension === 'svg';
                if (supportsAlphaMask) {
                    try {
                        const sanitizedMaskUrl = sanitizeUrl(originalMaskUrl);
                        foreignChild.style.maskImage = `url(${sanitizedMaskUrl})`;
                        foreignChild.style.maskRepeat = 'no-repeat';
                        foreignChild.style.maskSize = `${targetViewElement.width}px ${targetViewElement.height}px`;
                        foreignChild.style.maskPosition = `${x}px ${y}px`;
                        this._iosVideoMaskApplied = true;
                        return;
                    }
                    catch (_a) {
                        // Fall through to the non-alpha fallback
                    }
                }
                else {
                    // Fallback for non-alpha formats (e.g., JPG) or failed raster masks: use a rect mask.
                    const rect = this._document.createElementNS(SVG_NAMESPACE, 'rect');
                    rect.setAttribute('x', '0');
                    rect.setAttribute('y', '0');
                    rect.setAttribute('width', `${targetViewElement.width}`);
                    rect.setAttribute('height', `${targetViewElement.height}`);
                    rect.setAttribute('fill', 'white');
                    this._applyInlineSvgMask(foreignChild, rect, targetViewElement.width, targetViewElement.height, x, y);
                    this._iosVideoMaskApplied = true;
                }
            }
        }
        else {
            // For shape masks (ellipse, rectangle), clone the actual shape element
            const originalShape = targetSvgBackground === null || targetSvgBackground === void 0 ? void 0 : targetSvgBackground.shapeElement;
            if (originalShape) {
                const clippingShape = originalShape.cloneNode(true);
                clippingShape.removeAttribute('id');
                clippingShape.setAttribute('fill', 'white');
                this._applyInlineSvgMask(foreignChild, clippingShape, targetViewElement.width, targetViewElement.height, x, y);
                this._iosVideoMaskApplied = true;
            }
        }
    }
    _applyInlineSvgMask(foreignChild, shape, width, height, x, y) {
        const inlineSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${shape.outerHTML}</svg>`;
        const encodedSvg = encodeURIComponent(inlineSvg);
        const dataUrl = `url("data:image/svg+xml,${encodedSvg}")`;
        foreignChild.style.maskImage = dataUrl;
        foreignChild.style.maskRepeat = 'no-repeat';
        foreignChild.style.maskSize = `${width}px ${height}px`;
        foreignChild.style.maskPosition = `${x}px ${y}px`;
    }
    _applyMaskingStyles(maskingData) {
        var _a, _b;
        const { rotationX, rotationY, rotationZ, opacity, scaleX, scaleY, mirrorX, mirrorY } = maskingData;
        const matrix = new Matrix();
        matrix.perspective_m((_a = this._viewElement.perspective) !== null && _a !== void 0 ? _a : 800);
        matrix.scale_m(mirrorX ? -scaleX : scaleX, mirrorY ? -scaleY : scaleY);
        const is3DRotation = rotationX !== 0 || rotationY !== 0;
        const isMasked = this._data && isMaskedNode(this._data);
        const isMask = this._data && isMaskNode(this._data);
        const imageContainer = this._imageContainerElement;
        if (imageContainer && isMasked && !isMask) {
            if (is3DRotation) {
                const rx = toDegrees(rotationX);
                const ry = toDegrees(rotationY);
                const rz = toDegrees(rotationZ);
                imageContainer.style.transformOrigin = 'center center';
                imageContainer.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) rotateZ(${rz}deg)`;
                imageContainer.removeAttribute('transform');
            }
            else {
                const rz = toDegrees(rotationZ);
                const cx = this._width / 2;
                const cy = this._height / 2;
                /**
                 *  CSS transforms (using rotateZ(n) or matrix()/matrix3d()) always transform on a 3D plane.
                 *  The SVG transform attribute does not support the 3D plane (rotate(deg, x?, y?)), hence is more performant
                 */
                imageContainer.style.transform = '';
                imageContainer.setAttribute('transform', `rotate(${rz} ${cx} ${cy})`);
            }
        }
        else {
            matrix.rotateX_m(rotationX);
            matrix.rotateY_m(rotationY);
            matrix.rotateZ_m(rotationZ);
        }
        const svgNode = this.getMaskingNode();
        if (isImageNode(this._data) && isMaskNode(this._data)) {
            // Apply transform through style instead in order to avoid re-evaluation of image.
            // It can otherwise lead to performance issues.
            svgNode.style.transform = matrix.toCSS_m();
        }
        else {
            this._applySvgTransform(svgNode, matrix);
        }
        svgNode.style.opacity = `${opacity}`;
        svgNode.style.transformBox = 'border-box';
        if (this._data && isMaskNode(this._data) && this._imageElement) {
            // Keep image mask pivot at the visible frame center (frame center corrected by pan)
            const { x = 0, y = 0 } = (_b = this._image) !== null && _b !== void 0 ? _b : {};
            this._setMaskImageTransformOrigin(x, y);
        }
        else {
            svgNode.style.transformOrigin = 'center center';
        }
    }
    // Safari has issues with matrix3d for masked svg elements.
    // Initializing with a SVGMatrix & let the parent handle 3d transforms works
    // Convert the custom Matrix to an SVGMatrix
    _applySvgTransform(svgNode, matrix) {
        var _a;
        if (!svgNode.ownerSVGElement) {
            throw new Error('SVGNode is not attached to an SVGElement');
        }
        const svgMatrix = svgNode.ownerSVGElement.createSVGMatrix();
        const cssMatrix = (_a = matrixValueRegex.exec(matrix.toCSS_m())) === null || _a === void 0 ? void 0 : _a[1].split(',').map(parseFloat);
        if ((cssMatrix === null || cssMatrix === void 0 ? void 0 : cssMatrix.length) === 16) {
            svgMatrix.a = cssMatrix[0];
            svgMatrix.b = cssMatrix[1];
            svgMatrix.c = cssMatrix[4];
            svgMatrix.d = cssMatrix[5];
            svgMatrix.e = cssMatrix[12];
            svgMatrix.f = cssMatrix[13];
        }
        const transformList = svgNode.transform.baseVal;
        if (transformList.numberOfItems > 0) {
            transformList.getItem(0).setMatrix(svgMatrix);
        }
        else {
            const newTransform = svgNode.ownerSVGElement.createSVGTransform();
            newTransform.setMatrix(svgMatrix);
            transformList.initialize(newTransform);
        }
    }
    /**
     * Sets the transform origin for a mask image element to pivot around the visible frame center
     * @param x - The x offset from the image positioning
     * @param y - The y offset from the image positioning
     */
    _setMaskImageTransformOrigin(x, y) {
        if (this._data && isMaskNode(this._data) && this._imageElement) {
            const cx = this._width / 2 - x;
            const cy = this._height / 2 - y;
            this._imageElement.style.transformBox = 'border-box';
            this._imageElement.style.transformOrigin = `${cx}px ${cy}px`;
        }
    }
    getMaskingNode() {
        var _a, _b;
        return (_b = (_a = this._imageElement) !== null && _a !== void 0 ? _a : this._foreignObject) !== null && _b !== void 0 ? _b : this._shapeElement;
    }
    _getMaskingData() {
        var _a, _b, _c;
        const { x, y, opacity, scaleX, scaleY, mirrorX, mirrorY } = this._viewElement;
        const hidden = this._viewElement.__data.hidden;
        return {
            x,
            y,
            rotationX: (_a = this._viewElement.rotationX) !== null && _a !== void 0 ? _a : 0,
            rotationY: (_b = this._viewElement.rotationY) !== null && _b !== void 0 ? _b : 0,
            rotationZ: (_c = this._viewElement.rotationZ) !== null && _c !== void 0 ? _c : 0,
            scaleX,
            scaleY,
            mirrorX: mirrorX !== null && mirrorX !== void 0 ? mirrorX : false,
            mirrorY: mirrorY !== null && mirrorY !== void 0 ? mirrorY : false,
            opacity,
            hidden
        };
    }
    _shouldUpdateMask() {
        const dataNode = this._data;
        if (!dataNode || !isUsedInMask(dataNode)) {
            return false;
        }
        const renderMap = SVGBackground._maskingRenderMap;
        const maskMap = renderMap.get_m(dataNode);
        if (!maskMap) {
            renderMap.set_m(dataNode, false);
            return true;
        }
        if (maskMap === null || maskMap === void 0 ? void 0 : maskMap.changedOnPreviousPass) {
            return true;
        }
        return this._maskingDataIsChanged();
    }
    _maskingDataIsChanged() {
        const maskingData = this._getMaskingData();
        return (!this._maskingData ||
            this._maskingData.x !== maskingData.x ||
            this._maskingData.y !== maskingData.y ||
            this._maskingData.opacity !== maskingData.opacity ||
            this._maskingData.rotationX !== maskingData.rotationX ||
            this._maskingData.rotationY !== maskingData.rotationY ||
            this._maskingData.rotationZ !== maskingData.rotationZ ||
            this._maskingData.scaleX !== maskingData.scaleX ||
            this._maskingData.scaleY !== maskingData.scaleY ||
            this._maskingData.hidden !== maskingData.hidden ||
            this._maskingData.mirrorX !== maskingData.mirrorX ||
            this._maskingData.mirrorY !== maskingData.mirrorY);
    }
    removeMasking_m(keepMapEntry = false) {
        var _a, _b;
        if (!this._data || !isMaskingSupported(this._data)) {
            return;
        }
        this._moveSvgGroupElement();
        this._groupElement.removeAttribute('mask');
        this._groupElement.style.maskMode = '';
        const svgNode = this.getMaskingNode();
        svgNode.style.transform = '';
        svgNode.style.transformOrigin = '';
        svgNode.style.transformBox = '';
        svgNode.style.opacity = '';
        svgNode.style.position = '';
        svgNode.removeAttribute('transform');
        if (!keepMapEntry) {
            SVGBackground._maskingRenderMap.delete_m(this._data);
        }
        (_a = this._maskUseElement) === null || _a === void 0 ? void 0 : _a.remove();
        (_b = this._maskElement) === null || _b === void 0 ? void 0 : _b.remove();
        this._maskUseElement = undefined;
        this._maskElement = undefined;
        this._iosVideoMaskApplied = false;
        this._maskingData = undefined;
        if (this._imageContainerElement) {
            this._imageContainerElement.replaceWith(...this._imageContainerElement.childNodes);
            this._imageContainerElement = undefined;
        }
    }
    _setBorder(border) {
        var _a;
        let borderElement;
        const { thickness, style, color } = border || { thickness: 0, style: 'solid' };
        const colorString = color ? color.toString() : 'transparent';
        const thicknessChange = ((_a = this._border) === null || _a === void 0 ? void 0 : _a.thickness) !== thickness;
        // Identical border, don't update
        if (this._border &&
            !thicknessChange &&
            this._border.style === style &&
            this._border.color === colorString) {
            return;
        }
        this._border = {
            thickness,
            style,
            color: colorString
        };
        // Odd width border fix. Anti-aliasing.
        this._svgElement.style.boxShadow =
            thickness && this._shadowType !== SvgShadowType.BoxShadow
                ? `0 0 ${thickness}px transparent`
                : 'none';
        // Image is handled differently (need to be clipped and border is on the outside)
        if (this._shapeElementKind === ElementKind.Image || isVideoNode(this._data)) {
            borderElement = this._createBorderAndClipElement();
        }
        else {
            borderElement = this._shapeElement;
        }
        borderElement.setAttribute('stroke', this._border.color);
        borderElement.setAttribute('stroke-width', `${this._border.thickness}`);
        borderElement.setAttribute('stroke-linejoin', 'square');
        if (this._border.style === 'solid') {
            borderElement.setAttribute('stroke-linecap', 'butt');
            borderElement.setAttribute('stroke-dasharray', '');
        }
        else if (this._border.style === 'dashed') {
            borderElement.setAttribute('stroke-linecap', 'butt');
            borderElement.setAttribute('stroke-dasharray', `${thickness * 3}`);
        }
        else if (this._border.style === 'dotted') {
            borderElement.setAttribute('stroke-linecap', 'round');
            borderElement.setAttribute('stroke-dasharray', `1, ${thickness * 2}`);
        }
        // Update shadows when animating border size
        if (thicknessChange) {
            this._setCssShadows(this._shadows);
        }
        console.debug('[SVGBackground] _setBorder');
    }
    /**
     * A more performant way to show shadows.
     * Note that it doesn't support spread
     * @param shadows
     */
    _setCssShadows(shadows) {
        const type = this._shadowType;
        const style = this._svgElement.style;
        const willChange = style.willChange
            .split(',')
            .filter(f => !!f)
            .map(f => f.trim());
        // Cleanup shadows
        if (!(shadows === null || shadows === void 0 ? void 0 : shadows.length) || type === SvgShadowType.None) {
            if (willChange.indexOf('filter') > -1) {
                style.willChange = willChange.filter(f => f !== 'filter').join(', ');
                style.filter = '';
            }
        }
        else if (type === SvgShadowType.DropShadow) {
            if (willChange.indexOf('filter') === -1) {
                style.willChange = [...willChange, 'filter'].join(', ');
            }
            const dropShadows = shadows.map(s => `drop-shadow(${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.color})`);
            style.filter = dropShadows.join(' ');
            console.debug('[SVGBackground] _setCssShadows');
        }
    }
    /**
     * This is the only way to use shadow spread and still handle transparent pngs etc.
     * Beware that this approach is advanced and not even figma can handle spread well.
     * @param shadows
     * @returns
     */
    _setShadows(shadows = []) {
        var _a;
        if (shadows.length) {
            const newShadows = shadows.map(s => (Object.assign(Object.assign({}, s), { color: s.color.toString() })));
            const shadowType = this._getShadowType();
            let changed = this._shadows.length !== newShadows.length || shadowType !== this._shadowType;
            if (!changed) {
                changed = newShadows.some((s, i) => {
                    var _a;
                    const old = (_a = this._shadows) === null || _a === void 0 ? void 0 : _a[i];
                    return (!old ||
                        s.blur !== old.blur ||
                        s.spread !== old.spread ||
                        s.offsetX !== old.offsetX ||
                        s.offsetY !== old.offsetY ||
                        s.color !== old.color);
                });
            }
            // Trigger new shadow size update if length of shadows have changed
            if (newShadows.length !== ((_a = this._shadows) === null || _a === void 0 ? void 0 : _a.length)) {
                this._shadowSize = undefined;
            }
            this._shadows = newShadows;
            if (!changed) {
                return;
            }
            this._shadowType = shadowType;
            // Make svg more performant by using overflow: clip when possible
            this._setSvgOverflow();
            const groupId = this._getId(IdSuffix.Group);
            const { width, height } = this._element;
            const svgShadowLength = this._shadowType === SvgShadowType.SvgShadow ? shadows.length : 0;
            // Remove additional shadow element if count have changed
            while (this._shadowFilterElements.length > svgShadowLength) {
                const filter = this._shadowFilterElements.pop();
                const use = this._shadowUseElements.pop();
                filter === null || filter === void 0 ? void 0 : filter.remove();
                use === null || use === void 0 ? void 0 : use.remove();
            }
            // Set css filter drop shadow instead (much more performant)
            if (this._shadowType !== SvgShadowType.SvgShadow) {
                this._setCssShadows(this._shadows);
                // Might need to change back overflow size
                this._setShadowSize();
                return;
            }
            // reset css filter drop shadow
            this._setCssShadows();
            // Apply shadows backwards to get the correct z index...
            for (let i = 0; i < shadows.length; i++) {
                const shadow = shadows[i];
                const color = shadow.color;
                const filterId = this._getId(IdSuffix.Shadow) + i;
                let filterElement = this._shadowFilterElements[i];
                let useElement = this._shadowUseElements[i];
                if (!filterElement) {
                    // Note that the CSS drop-shadow filter doesn't support spread
                    filterElement = this._document.createElementNS(SVG_NAMESPACE, 'filter');
                    filterElement.id = filterId;
                    filterElement.setAttribute('color-interpolation-filters', 'sRGB');
                    filterElement.setAttribute('width', `300%`);
                    filterElement.setAttribute('height', `300%`);
                    setPosition(filterElement, -100, -100, '%');
                    // Do a solid color with the same shape as the element (works for transparent pngs as well)
                    const matrix = this._document.createElementNS(SVG_NAMESPACE, 'feColorMatrix');
                    matrix.setAttribute('type', 'matrix');
                    matrix.setAttribute('in', 'SourceGraphic');
                    matrix.setAttribute('result', `${filterId}_color`);
                    filterElement.appendChild(matrix);
                    // Blur that shape
                    const blur = this._document.createElementNS(SVG_NAMESPACE, 'feGaussianBlur');
                    blur.setAttribute('type', 'matrix');
                    blur.setAttribute('result', `${filterId}_blur`);
                    blur.setAttribute('in', `${filterId}_color`);
                    filterElement.appendChild(blur);
                    this._shadowFilterElements.push(filterElement);
                    this._defsElement.appendChild(filterElement);
                }
                if (!useElement) {
                    useElement = this._document.createElementNS(SVG_NAMESPACE, 'use');
                    useElement.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', `${this._baseUrl}#${groupId}`); // Will not render <use> tag if this isn't set
                    useElement.setAttribute('filter', `url(${this._baseUrl}#${filterId})`);
                    useElement.setAttribute('width', '100%');
                    useElement.setAttribute('height', '100%');
                    setPosition(useElement, 0, 0);
                    useElement.style.transformOrigin = '50% 50%';
                    this._shadowUseElements.push(useElement);
                    insertAfter(this._defsElement, useElement);
                }
                // Only way that works in Edge, example: https://codepen.io/mullany/pen/sJopz
                const colorMatrix = `0 0 0 0 ${color.red / 255} 0 0 0 0 ${color.green / 255} 0 0 0 0 ${color.blue / 255} 0 0 0 ${color.alpha / 100} 0`;
                filterElement
                    .getElementsByTagName('feColorMatrix')[0]
                    .setAttribute('values', colorMatrix);
                filterElement
                    .getElementsByTagName('feGaussianBlur')[0]
                    .setAttribute('stdDeviation', `${shadow.blur}`);
                setPosition(useElement, shadow.offsetX, shadow.offsetY);
                // Use scale to set spread since it's not supported in SVG
                useElement.style.transform = `scaleX(${1 + shadow.spread / width}) scaleY(${1 + shadow.spread / height})`;
            }
            this._setShadowSize();
            console.debug('[SVGBackground] _setShadows');
        }
    }
    _getSvgFillOption(data) {
        const sizeMode = data.imageSettings.sizeMode;
        if (!sizeMode) {
            if (data.ratio) {
                return 'xMidYMid slice';
            }
            else if (!data.ratio) {
                return 'none';
            }
            return 'xMidYMid meet';
        }
        return SVG_IMAGE_OPTIONS[sizeMode];
    }
    async _setImageUrl(url) {
        var _a, _b, _c;
        if (url === this._url) {
            return;
        }
        this._url = url;
        const clipId = this._getId(IdSuffix.Clip);
        if (!this._imageElement) {
            const imageElement = this._document.createElementNS(SVG_NAMESPACE, 'image');
            imageElement.id = this._getId(IdSuffix.Image);
            setPosition(imageElement, 0, 0);
            this._imageElement = imageElement;
            this._addMaskedImageContainer(this._imageElement);
            this._createBorderAndClipElement();
            this._groupElement.insertBefore((_a = this._imageContainerElement) !== null && _a !== void 0 ? _a : this._imageElement, this._borderUseElement);
            imageElement.setAttribute('clip-path', `url(${this._baseUrl}#${clipId})`);
        }
        if (url && isImageNode(this._data)) {
            // Show loader when uploading images to canvas
            if ((_b = this._data.imageAsset) === null || _b === void 0 ? void 0 : _b.__loading) {
                this._showLoader();
            }
            const isMasked = isMaskedNode(this._data);
            await this._loadImage(url, isMasked);
            // __loading is set to false after upload to canvas is completed
            if (!((_c = this._data.imageAsset) === null || _c === void 0 ? void 0 : _c.__loading)) {
                this._hideLoader();
            }
        }
        else {
            this._clearImageElement(true /** imageWasRemoved */);
        }
    }
    _loadImage(url, isMasked) {
        // Make sure to never set any js or other potential treat as src
        url = sanitizeUrl(url);
        if (!this._imageElement || !this._url) {
            throw new Error('No image element added to svg');
        }
        const version = getBrowserVersion();
        // Reset the src attribute to force the browser to reload the image
        this._clearImageElement();
        if ((isIOS && isMasked) || (isSafari && version < 14) || (isEdge && version < 20)) {
            // Load events on SVG <image> elements don't work reliably in certain browsers,
            // especially for masked images in iOS (any browser) and old versions of Safari/Edge.
            // This forces a fallback to HTML <img> preloading to ensure the image is fully loaded.
            const promiseResolver = loadImagePromise(url);
            promiseResolver.promise.catch((e) => {
                this._clearImageElement();
                promiseResolver.reject(new ImageError(ErrorMessage.ImageLoadingFailed, url, e));
            });
            this._imageLoadPromise = promiseResolver;
        }
        else {
            const promiseResolver = new PromiseResolver();
            this._imageElement.addEventListener('load', () => promiseResolver.resolve(this._imageElement));
            this._imageElement.addEventListener('error', (e) => {
                this._clearImageElement();
                promiseResolver.reject(new ImageError(ErrorMessage.ImageLoadingFailed, url, e));
            });
            this._imageLoadPromise = promiseResolver;
        }
        this._imageElement.setAttributeNS(XLINK_NAMESPACE, 'xlink:href', url);
        this._imageElement.setAttribute('src', url);
        if (isSafari && isMasked) {
            this._syncImageLoadPromise();
        }
        return this._imageLoadPromise.promise;
    }
    _clearImageElement(imageWasRemoved = false) {
        if (!this._imageElement) {
            return;
        }
        if (this._imageLoadPromise && imageWasRemoved) {
            this._imageLoadPromise.reject(ErrorMessage.ImageRemovedDuringLoad);
        }
        this._imageElement.removeAttributeNS(XLINK_NAMESPACE, 'xlink:href');
        this._imageElement.removeAttribute('xlink:href');
        this._imageElement.removeAttribute('src');
    }
    /**
     * Safari can trigger multiple reloads for masked image elements before earlier promise settles.
     * This method updates the renderer's preload map so it tracks the promise from the latest load.
     */
    _syncImageLoadPromise() {
        if (!this._imageLoadPromise || !isViewElementNode(this._element)) {
            return;
        }
        const elementId = this._element.id;
        if (!elementId || !this._renderer.preloadingElements.has(elementId)) {
            return;
        }
        // Update the preload map with the latest image load promise
        this._renderer.preloadingElements.set(elementId, this._imageLoadPromise.promise.then(() => undefined));
    }
    // TODO: Maybe prevent from redrawing every time
    _setImageSize() {
        if (this._shapeElementKind === ElementKind.Image && this._imageElement) {
            const viewElement = this._styles;
            const dataNode = viewElement.__data;
            const svgElement = this._imageElement;
            const fillOption = this._getSvgFillOption(dataNode);
            const isCropped = ImageOptimizerUrlBuilder.isCropped_m(viewElement);
            const fitPosition = this._getSvgImagePosition(viewElement, dataNode.imageSettings, dataNode.imageAsset, !!viewElement.feed, isCropped);
            let x = Math.min(fitPosition.x, 0);
            let y = Math.min(fitPosition.y, 0);
            let width = Math.max(this._width + Math.abs(fitPosition.x), 0);
            let height = Math.max(this._height + Math.abs(fitPosition.y), 0);
            if (dataNode.imageSettings.sizeMode !== ImageSizeMode.Fill) {
                x = 0;
                y = 0;
                width = this._width;
                height = this._height;
            }
            // Setting an attribute makes chrome reload image when user have disabled cache
            if (this._image &&
                this._image.x === x &&
                this._image.y === y &&
                this._image.width === width &&
                this._image.height === height &&
                this._image.fillOption === fillOption) {
                return;
            }
            this._shapeElement.style.overflow =
                dataNode.imageSettings.sizeMode === ImageSizeMode.Fill ? 'hidden' : 'visible';
            svgElement.setAttribute('width', `${width}`);
            svgElement.setAttribute('height', `${height}`);
            svgElement.setAttribute('preserveAspectRatio', fillOption);
            const overflow = this._getShadowOverflow();
            setPosition(svgElement, x + overflow, y + overflow);
            if (this._borderElement) {
                setPosition(this._borderElement, overflow, overflow);
            }
            this._image = { x, y, width, height, fillOption };
            // If the image is used as mask, pivot the mask image around
            // the center of the visible frame.
            this._setMaskImageTransformOrigin(x, y);
            console.debug('[SVGBackground] _setImageSize');
        }
    }
    /**
     * Make svg bigger than element to accommodate Chrome 87 and FF changes to svg filter clipping.
     */
    _setShadowSize() {
        if (this._shadows.length === 0) {
            return;
        }
        const svgElement = this._svgElement;
        const shadows = this._shadows || [];
        const { width, height } = this._element;
        const overflow = this._getShadowOverflow();
        const shadowWidth = Math.ceil(width + overflow * 2);
        const shadowHeight = Math.ceil(height + overflow * 2);
        const shadowSize = { width: shadowWidth, height: shadowHeight };
        if (!this._shadowSize || !isSameSize(this._shadowSize, shadowSize)) {
            this._shadowSize = shadowSize;
            svgElement.style.left = `-${overflow}px`;
            svgElement.style.top = `-${overflow}px`;
            svgElement.style.width = `${shadowWidth}px`;
            svgElement.style.height = `${shadowHeight}px`;
            svgElement.setAttribute('width', `${shadowWidth}`);
            svgElement.setAttribute('height', `${shadowHeight}`);
            if (this._shapeElementKind === ElementKind.Ellipse) {
                this._shapeElement.setAttribute('cx', `${shadowWidth / 2}px`);
                this._shapeElement.setAttribute('cy', `${shadowHeight / 2}px`);
            }
            else {
                setPosition(this._shapeElement, overflow, overflow);
                if (this._shapeElementKind === ElementKind.Image && this._image) {
                    if (this._imageElement) {
                        setPosition(this._imageElement, this._image.x + overflow, this._image.y + overflow);
                    }
                    if (this._borderElement) {
                        setPosition(this._borderElement, overflow, overflow);
                    }
                }
            }
            if (this._getShadowType() !== SvgShadowType.SvgShadow) {
                this._setCssShadows(shadows);
            }
            else {
                for (let i = 0; i < shadows.length; i++) {
                    const shadow = shadows[i];
                    const useElement = this._shadowUseElements[i];
                    // Note: our spread values might be considered wrong 1px make the shadow 1px wider and taller (should be double)
                    useElement.style.transform = `scaleX(${1 + shadow.spread / width}) scaleY(${1 + shadow.spread / height})`;
                }
            }
            console.debug('[SVGBackground] _setShadowSize');
        }
    }
    /**
     * Make svg more performant by using overflow: clip when possible
     */
    _setSvgOverflow() {
        var _a;
        const type = this._shadowType;
        const border = (_a = this._border) === null || _a === void 0 ? void 0 : _a.thickness;
        const clip = (type === SvgShadowType.None && !border) || type === SvgShadowType.SvgShadow;
        // Clip is not supported in all browsers so fallback on visible
        this._svgElement.style.overflow = clip ? 'clip' : 'visible';
        console.debug('[SVGBackground] _setSvgOverflow');
    }
    _getSvgImagePosition(element, imageSettings, imageAsset, isFeeded, isCropped) {
        let x = 0;
        let y = 0;
        if (!isFeeded && !isCropped && (imageAsset === null || imageAsset === void 0 ? void 0 : imageAsset.width) && (imageAsset === null || imageAsset === void 0 ? void 0 : imageAsset.height)) {
            // Image size on canvas including the hidden/overflow areas
            const renderedImageSize = Object.assign(Object.assign({}, aspectRatioScale(imageAsset, element, 'cover')), { x: 0, y: 0 });
            const overflowX = renderedImageSize.width - element.width;
            const overflowY = renderedImageSize.height - element.height;
            // Note: Note that these values are x2 larger than rendered in the svg due to hack needed
            if (overflowX > 0) {
                // fitPositionX = -1 meeans that we move the image 0.5px left
                x = Math.round((1 - imageSettings.x * 2) * overflowX);
            }
            if (overflowY > 0) {
                // fitPositionY = -1 meeans that we move the image 0.5px up
                y = Math.round((1 - imageSettings.y * 2) * overflowY);
            }
        }
        return { x, y };
    }
    _getShadowOverflow() {
        var _a;
        // Only svg shadows need overflow
        if (this._getShadowType() !== SvgShadowType.SvgShadow) {
            return 0;
        }
        const shadows = this._getAllShadowsFromDataElement();
        const borderOverflow = ((_a = this._element.border) === null || _a === void 0 ? void 0 : _a.thickness) || 0;
        const shadowOverflow = shadows.reduce((max, s) => {
            const offset = Math.max(Math.abs(s.offsetX), Math.abs(s.offsetY));
            return Math.max(max, offset + s.blur * 2 + s.spread);
        }, 0);
        return borderOverflow + shadowOverflow;
    }
    _getShadowType() {
        const data = this._data;
        const shadows = this._getAllShadowsFromDataElement();
        if (shadows.length && data) {
            const hasSpread = shadows.some(s => s.spread > 0);
            // We can use css filter as long the shadow doesn't have spread
            if (!hasSpread) {
                return SvgShadowType.DropShadow;
            }
            // FF can't animate shadows without cropping them
            if (isFirefox && shadows.length > 1) {
                return SvgShadowType.DropShadow;
            }
            return SvgShadowType.SvgShadow;
        }
        return SvgShadowType.None;
    }
    _getAllShadowsFromDataElement() {
        const data = this._data;
        if (data) {
            return [...data.states.flatMap(s => s.shadows || []), ...(data.shadows || [])];
        }
        return [];
    }
    _getId(suffix) {
        return this._id + suffix;
    }
}
SVGBackground._maskingRenderMap = new MaskingRenderMap();
function setPosition(element, x, y, unit = 'px') {
    const xString = x + unit;
    const yString = y + unit;
    if (element.getAttribute('x') !== xString) {
        element.setAttribute('x', xString);
    }
    if (element.getAttribute('y') !== yString) {
        element.setAttribute('y', yString);
    }
    console.debug('[SVGBackground] setPosition');
}
export function createSVGLoaderImage(size, document = window.document) {
    const wrapperGroup = document.createElementNS(SVG_NAMESPACE, 'g');
    const circleGroup = document.createElementNS(SVG_NAMESPACE, 'g');
    circleGroup.setAttribute('transform', `translate(${size.width / 2 - 12}, ${size.height / 2 - 12})`);
    circleGroup.setAttribute('stroke', '#ffffff');
    circleGroup.setAttribute('stroke-width', '2');
    circleGroup.setAttribute('fill', 'none');
    const circleElement = document.createElementNS(SVG_NAMESPACE, 'circle');
    circleElement.setAttribute('stroke-opacity', '0.25');
    circleElement.setAttribute('cx', '12');
    circleElement.setAttribute('cy', '12');
    circleElement.setAttribute('r', '12');
    const pathElement = document.createElementNS(SVG_NAMESPACE, 'path');
    pathElement.setAttribute('d', 'M24 12c0-9.94-8.06-12-12-12');
    const animateTransform = document.createElementNS(SVG_NAMESPACE, 'animateTransform');
    animateTransform.setAttribute('attributeName', 'transform');
    animateTransform.setAttribute('type', 'rotate');
    animateTransform.setAttribute('from', '0 12 12');
    animateTransform.setAttribute('to', '360 12 12');
    animateTransform.setAttribute('dur', '1s');
    animateTransform.setAttribute('repeatCount', 'indefinite');
    pathElement.appendChild(animateTransform);
    circleGroup.appendChild(circleElement);
    circleGroup.appendChild(pathElement);
    const backgroundElement = document.createElementNS(SVG_NAMESPACE, 'rect');
    backgroundElement.setAttribute('width', `${size.width}`);
    backgroundElement.setAttribute('height', `${size.height}`);
    backgroundElement.setAttribute('fill', 'rgba(0,0,0,0.25)');
    wrapperGroup.appendChild(backgroundElement);
    wrapperGroup.appendChild(circleGroup);
    return wrapperGroup;
}
var SvgShadowType;
(function (SvgShadowType) {
    SvgShadowType[SvgShadowType["None"] = 0] = "None";
    SvgShadowType[SvgShadowType["DropShadow"] = 1] = "DropShadow";
    SvgShadowType[SvgShadowType["BoxShadow"] = 2] = "BoxShadow";
    SvgShadowType[SvgShadowType["SvgShadow"] = 3] = "SvgShadow";
})(SvgShadowType || (SvgShadowType = {}));
